import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import DeckGL from '@deck.gl/react';
import { WebMercatorViewport } from '@deck.gl/core';
import { Map as StaticMap, Marker } from 'react-map-gl';
import {
	combineLayers,
	generateGeoJsonIconLayer,
	generateInfrastructurePaths,
	generateTileLayers,
	hideLayer,
	orderLayers,
	parseAssetData,
	removeLayers,
	getImpactStatus,
	getSvgIconUrl,
} from '../../../utils';
import Supercluster from 'supercluster';
import StackedAssetModal from '../../../components/NWL/StackAssetModal/StackedAssetModal';
import './map.css';
import { IconLayer } from '@deck.gl/layers';
import BLOCKAGE_ICON from '/img/nwlmap/BlockageAlert.svg?url';
import ANOMALY_ICON from '/img/nwlmap/AnomalyAlert.svg?url';
import PUMP_ICON from '/img/nwlmap/PumpAlert.svg?url';
import BLOCKAGE_ANOMALY_ICON from '/img/nwlmap/blockage_anomaly.svg?url';
import BLOCKAGE_PUMP_ICON from '/img/nwlmap/blockage_pump.svg?url';
import ANOMALY_PUMP_ICON from '/img/nwlmap/anomaly_pump.svg?url';
import BLOCKAGE_ANOMALY_PUMP_ICON from '/img/nwlmap/blockage_anomaly_pump.svg?url';

const ICON_Z_INDEX = 3;
const INFRASTRUCTURE_Z_INDEX = 0;
const WEATHER_Z_INDEX = 0;
const SITES_Z_INDEX = 2;
const BADGE_Z_INDEX = 4;
const NETWORK_NODE_MIN_ZOOM = 14;

const CLUSTER_RADIUS = Number(process.env.VITE_CLUSTER_RADIUS) || 30;
const CLUSTER_MIN_ZOOM = Number(process.env.VITE_CLUSTER_MIN_ZOOM) || 2;
const CLUSTER_MAX_ZOOM = Number(process.env.VITE_CLUSTER_MAX_ZOOM) || 30;
const CLUSTER_STACKED_ASSET_ZOOM = Number(process.env.VITE_CLUSTER_STACKED_ASSET_ZOOM) || 26;

const rawSettings = localStorage.getItem('organizationSettings');
const mapOrganizationSettings = rawSettings ? JSON.parse(rawSettings) : {};
const MAPBOX_MAP_STYLE =
	mapOrganizationSettings?.mapSettings?.settings?.style || 'mapbox://styles/mapbox/outdoors-v12';
const MAP_CONTROLLER_OPTIONS = {
	scrollZoom: true,
	dragPan: true,
	dragRotate: true,
	doubleClickZoom: true,
	touchZoom: true,
	touchRotate: false,
	keyboard: true,
};

const STATUS_LABEL = {
	ONLINE: 'Online',
	AT_RISK: 'At risk',
	IMPACTED: 'Impacted',
	OFFLINE: 'Offline',
};

const STATUS_COLOR = {
	ONLINE: '#10B981',
	AT_RISK: '#F59E0B',
	IMPACTED: '#EF4444',
	OFFLINE: '#6B7280',
};

const DEFAULT_LAYER_STATE = {
	blockage: true,
	anomaly: true,
	pumpAlert: true,
	rainfall: true,
};

const kindFromFlags = (hasBlock, hasAnom, hasPump) => {
	if (hasBlock && hasAnom && hasPump) return 'BLOCKAGE_ANOMALY_PUMP';
	if (hasBlock && hasAnom) return 'BLOCKAGE_ANOMALY';
	if (hasBlock && hasPump) return 'BLOCKAGE_PUMP';
	if (hasAnom && hasPump) return 'ANOMALY_PUMP';
	if (hasBlock) return 'BLOCKAGE';
	if (hasAnom) return 'ANOMALY';
	if (hasPump) return 'PUMP';
	return null;
};

const flagsFromKind = (kind) => {
	switch (kind) {
		case 'BLOCKAGE':
			return { hasBlock: true, hasAnom: false, hasPump: false };
		case 'ANOMALY':
			return { hasBlock: false, hasAnom: true, hasPump: false };
		case 'PUMP':
			return { hasBlock: false, hasAnom: false, hasPump: true };
		case 'BLOCKAGE_ANOMALY':
			return { hasBlock: true, hasAnom: true, hasPump: false };
		case 'BLOCKAGE_PUMP':
			return { hasBlock: true, hasAnom: false, hasPump: true };
		case 'ANOMALY_PUMP':
			return { hasBlock: false, hasAnom: true, hasPump: true };
		case 'BLOCKAGE_ANOMALY_PUMP':
			return { hasBlock: true, hasAnom: true, hasPump: true };
		default:
			return { hasBlock: false, hasAnom: false, hasPump: false };
	}
};

const getRainGaugeEnabled = (layerState) => {
	if (!layerState) return true;

	if ('rain gauge' in layerState) {
		return !!layerState['rain gauge'];
	}

	return true;
};

const toLayerKey = (raw) => {
	if (!raw) return '';
	const lower = String(raw).trim().toLowerCase();
	const spaced = lower.replace(/_/g, ' ');
	if (spaced === 'rain gauge') return 'rain gauge';
	return lower;
};

const assetMatchesLayerFilter = (asset, layerState) => {
	const state = layerState || DEFAULT_LAYER_STATE;
	const raw = asset?.infrastructureTypeName ?? asset?.type;
	if (!raw) return true;

	const key = toLayerKey(raw);

	if (key === 'rain gauge') {
		return getRainGaugeEnabled(state);
	}

	if (Object.prototype.hasOwnProperty.call(state, key)) {
		return !!state[key];
	}

	return true;
};

const filterSiteByAlertLayer = (site, layerState) => {
	const state = layerState || DEFAULT_LAYER_STATE;

	const blockages = site.predictions?.blockages ?? [];
	const anomalies = site.predictions?.anomalies ?? [];
	const pumpAlerts = site.predictions?.pumps ?? [];

	const hasBlockRaw = blockages.some((b) => b.isLive);
	const hasAnomRaw = anomalies.some((a) => a.isLive);
	const hasPumpRaw = pumpAlerts.some((p) => p.isLive);

	const hasBlock = hasBlockRaw && state.blockage;
	const hasAnom = hasAnomRaw && state.anomaly;
	const hasPump = hasPumpRaw && state.pumpAlert;

	if (!hasBlock && !hasAnom && !hasPump) return null;

	return { hasBlock, hasAnom, hasPump };
};

const siteTooltip = (d) => {
	let label;

	switch (d.kind) {
		case 'BLOCKAGE':
			label = 'Blockage alert';
			break;
		case 'ANOMALY':
			label = 'Anomaly alert';
			break;
		case 'PUMP':
			label = 'Pump alert';
			break;
		case 'BLOCKAGE_ANOMALY':
			label = 'Blockage & anomaly alert';
			break;
		case 'BLOCKAGE_PUMP':
			label = 'Blockage & pump alert';
			break;
		case 'ANOMALY_PUMP':
			label = 'Anomaly & pump alert';
			break;
		case 'BLOCKAGE_ANOMALY_PUMP':
			label = 'Blockage, anomaly & pump alert';
			break;
		default:
			label = 'Alert';
	}

	return `
  <div style="color:#2A2A26; padding:2px 4px;" id="nwl-site-tooltip">
    <div style="font-weight:600; margin-bottom:3px; font-size: 16px;">${label}</div>
    <div style="color:#6B7280; font-size: 14px;">${d.type.toUpperCase()}</div>
    <div>
      <span style="display:flex; align-items:center; gap:4px;">
        <svg width="14" height="14" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="height:18px; width:18px;">
          <path d="M2 9.46777V2.96777C2 2.41549 2.44772 1.96777 3 1.96777H6.5C6.77614 1.96777 7 2.19163 7 2.46777C7 2.74392 6.77614 2.96777 6.5 2.96777H3V9.46777H9.5V5.96777C9.5 5.69163 9.72386 5.46777 10 5.46777C10.2761 5.46777 10.5 5.69163 10.5 5.96777V9.46777C10.5 10.0201 10.0523 10.4678 9.5 10.4678H3C2.44772 10.4678 2 10.0201 2 9.46777Z" fill="#6B7280"/>
          <path d="M11.0832 1.14685C11.033 1.12471 10.9789 1.1127 10.9241 1.1115L7.97781 1.1115C7.92287 1.1115 7.86847 1.12232 7.81771 1.14334C7.76695 1.16437 7.72083 1.19519 7.68198 1.23404C7.60352 1.3125 7.55944 1.41891 7.55944 1.52987C7.55944 1.58481 7.57026 1.63921 7.59129 1.68997C7.61231 1.74073 7.64313 1.78685 7.68198 1.8257C7.76044 1.90416 7.86685 1.94824 7.97781 1.94824L9.91941 1.94529L6.26897 5.59573C6.19083 5.67387 6.14693 5.77985 6.14693 5.89036C6.14693 6.00087 6.19083 6.10685 6.26897 6.18499C6.34711 6.26313 6.45309 6.30703 6.5636 6.30703C6.67411 6.30703 6.78009 6.26313 6.85823 6.18499L10.5087 2.53455L10.5057 4.47615C10.5055 4.53115 10.5162 4.58566 10.5371 4.63652C10.5581 4.68738 10.5889 4.73359 10.6278 4.77248C10.6667 4.81138 10.7129 4.84219 10.7637 4.86313C10.8146 4.88408 10.8691 4.89474 10.9241 4.89452C10.9791 4.89474 11.0336 4.88408 11.0845 4.86313C11.1353 4.84219 11.1815 4.81138 11.2204 4.77248C11.2593 4.73359 11.2901 4.68738 11.3111 4.63652C11.332 4.58566 11.3427 4.53115 11.3425 4.47615L11.3425 1.52987C11.3413 1.47503 11.3293 1.42096 11.3071 1.37077C11.2648 1.26957 11.1844 1.18912 11.0832 1.14685Z" fill="#6B7280"/>
        </svg>
        <span style="color:#6B7280; font-size: 14px">Details open in Storm Harvester</span>
      </span>
    </div>
  </div>
`;
};

function svgUrlToPngDataUrl(svgUrl, size = 64) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = size;
			canvas.height = size;
			const ctx = canvas.getContext('2d');
			ctx.clearRect(0, 0, size, size);
			const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
			const dw = Math.round(img.naturalWidth * scale);
			const dh = Math.round(img.naturalHeight * scale);
			const dx = Math.floor((size - dw) / 2);
			const dy = Math.floor((size - dh) / 2);
			ctx.drawImage(img, dx, dy, dw, dh);
			try {
				resolve(canvas.toDataURL('image/png'));
			} catch (e) {
				reject(e);
			}
		};
		img.onerror = reject;
		img.src = svgUrl;
	});
}

const siteSizeForZoom = (z) => {
	const z1 = 8,
		z2 = 12,
		z3 = 16;
	const s1 = 20,
		s2 = 22,
		s3 = 24;
	const lerp = (a, b, t) => Math.round(a + (b - a) * t);
	if (z <= z1) return s1;
	if (z <= z2) return lerp(s1, s2, (z - z1) / (z2 - z1));
	if (z <= z3) return lerp(s2, s3, (z - z2) / (z3 - z2));
	return s3;
};

const badgeOffsetForZoom = (z) => {
	const LOW = [14, -20];
	const MID = [17, -32];
	const HIGH = [22, -30];

	const z1 = 8,
		z2 = 12,
		z3 = 16;

	const lerp = (a, b, t) => [
		Math.round(a[0] + (b[0] - a[0]) * t),
		Math.round(a[1] + (b[1] - a[1]) * t),
	];

	if (z <= z1) return HIGH;
	if (z <= z2) return MID;
	if (z >= z3) return LOW;

	if (z <= z2) {
		const t = (z - z1) / (z2 - z1);
		return lerp(HIGH, MID, t);
	}
	const t = (z - z2) / (z3 - z2);
	return lerp(MID, LOW, t);
};

const clusterMarkerRadiusPx = (count = 1) => {
	const base = 30;
	const extra = Math.min(18, Math.log2(Math.max(count, 1)) * 3);
	return base + extra;
};

const clampOffsetToCircle = (offset, radiusPx) => {
	const [x, y] = offset;
	const mag = Math.hypot(x, y);
	const max = Math.max(0, radiusPx - 4);
	if (mag === 0 || mag <= max) return [Math.round(x), Math.round(y)];
	const k = max / mag;
	return [Math.round(x * k), Math.round(y * k)];
};

const hasNetworkData = (links) => {
	if (!links) return false;

	if (Array.isArray(links)) {
		return links.length > 0;
	}

	if (links.type === 'FeatureCollection') {
		return Array.isArray(links.features) && links.features.length > 0;
	}

	if (links.type === 'Feature') {
		return !!links.geometry;
	}

	if (Array.isArray(links.features)) {
		return links.features.length > 0;
	}

	if (Array.isArray(links.data)) {
		return links.data.length > 0;
	}

	if (Array.isArray(links.links)) {
		return links.links.length > 0;
	}

	return false;
};

const Map = ({
	viewState,
	setViewState,
	infrastructure,
	links,
	sites,
	timeBarSelection,
	showWeatherOverlay,
	weatherOverlayKey,
	children,
	onItemSelected,
	predictedImpacts,
	isDefault,
	layerState,
	activeLocation,
}) => {
	const mapRef = useRef(null);
	const [layers, setLayers] = useState([]);
	const [clusteredAssets, setClusteredAssets] = useState(null);
	const [stackedAssetInfo, setStackedAssetInfo] = useState(null);
	const sitePointsRef = useRef([]);
	const [badgeAtlases, setBadgeAtlases] = useState(null);
	const [badgePoints, setBadgePoints] = useState([]);
	const [hiddenSiteIds, setHiddenSiteIds] = useState(new Set());
	const [siteIndex, setSiteIndex] = useState(null);
	const assetIndexRef = useRef(new globalThis.Map());
	const [focusPin, setFocusPin] = useState(null);
	const deckRef = useRef(null);
	const [focusHasAsset, setFocusHasAsset] = useState(false);
	const deckHostRef = useRef(null);

	const syncBoundsFromMap = useCallback(() => {
		const map = mapRef.current?.getMap?.();
		if (!map) return;

		const b = map.getBounds();
		const bounds = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];

		setViewState((prev) => {
			if (prev.bounds && prev.bounds.every((v, i) => v === bounds[i])) {
				return prev;
			}
			return { ...prev, bounds };
		});
	}, [setViewState]);

	const assetIndex = useMemo(() => {
		const pickLastCoord = (v) => (Array.isArray(v) ? v.at(-1) : v);
		const idx = new globalThis.Map();

		if (Array.isArray(infrastructure)) {
			for (const a of infrastructure) {
				const { latitude, longitude } = pickLastCoord(a?.coordinates) || {};
				const key = a?.infrastructureId ?? a?.measurementeInfraestructureId ?? a?.id;

				const lon = Number(longitude);
				const lat = Number(latitude);

				if (key != null && Number.isFinite(lon) && Number.isFinite(lat)) {
					idx.set(String(key), {
						position: [lon, lat],
						asset: a,
					});
				}
			}
		}

		return idx;
	}, [infrastructure]);

	const getClusterChildren = (id) => clusteredAssets.getLeaves(id, Infinity);

	const handleViewStateChange = useCallback(
		(viewState) => {
			const newViewState = {
				...viewState,
				bounds: new WebMercatorViewport({ ...viewState }).getBounds(),
			};
			setViewState(newViewState);
			return newViewState;
		},
		[setViewState],
	);

	const getImpactedStatusNow = useCallback(
		(assetId) => {
			if (isDefault) return false;
			const timestamp = Number(timeBarSelection);
			return getImpactStatus(predictedImpacts, [assetId], timestamp);
		},
		[predictedImpacts, timeBarSelection, isDefault],
	);

	// ======= Tooltip (assets) shows "| Blockage/Anomaly alert" based on sites =======
	const siteAlerts = useMemo(() => {
		const out = Object.create(null);
		(sites ?? []).forEach((s) => {
			const infraId = s?.physicalStructure?.id;
			if (!infraId) return;

			const filtered = filterSiteByAlertLayer(s, layerState);
			if (!filtered) return;
			const { hasBlock, hasAnom, hasPump } = filtered;
			const kind = kindFromFlags(hasBlock, hasAnom, hasPump);
			if (kind) {
				out[String(infraId)] = kind;
			}
		});
		return out;
	}, [sites, layerState]);

	const assetTooltipHtml = (p) => {
		const name = p?.name ?? '';
		const type = p?.type ?? '';
		const hasForecast = p?.measurementTarget?.hasForecast;
		const statusKey = String(p?.status || 'ONLINE').toUpperCase();
		const statusText = STATUS_LABEL[statusKey] || 'Online';
		const statusColor = STATUS_COLOR[statusKey] || STATUS_COLOR.ONLINE;

		const ids = p?.id != null ? [p.id] : [];
		const alert = ids.map((id) => siteAlerts[id]).find(Boolean);
		let sitePredictionAlert = '';
		if (alert) {
			let alertText;
			switch (alert) {
				case 'BLOCKAGE':
					alertText = 'Blockage alert';
					break;
				case 'ANOMALY':
					alertText = 'Anomaly alert';
					break;
				case 'PUMP':
					alertText = 'Pump alert';
					break;
				case 'BLOCKAGE_ANOMALY':
					alertText = 'Blockage & anomaly alert';
					break;
				case 'BLOCKAGE_PUMP':
					alertText = 'Blockage & pump alert';
					break;
				case 'ANOMALY_PUMP':
					alertText = 'Anomaly & pump alert';
					break;
				case 'BLOCKAGE_ANOMALY_PUMP':
					alertText = 'Blockage, anomaly & pump alert';
					break;
				default:
					alertText = 'Alert';
			}
			sitePredictionAlert = ` | ${alertText}`;
		}

		if (!isDefault && !hasForecast) {
			return `
        <div data-testid="tooltip${name}" id="tooltip-${name}"
            style="color:#111827; min-width:180px; padding:6px 8px;">
          <div style="font-weight:600; margin-bottom:2px; font-size:16px;">${name}</div>
          <div style="color:#6B7280; font-size:12px; margin-bottom:4px;">${type}</div>
          <div style="display:flex; align-items:center; gap:4px; font-size:13px; color:#077898;">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="#077898" stroke-width="1.2"/>
              <path d="M8 4.5C7.65482 4.5 7.375 4.77982 7.375 5.125C7.375 5.47018 7.65482 5.75 8 5.75C8.34518 5.75 8.625 5.47018 8.625 5.125C8.625 4.77982 8.34518 4.5 8 4.5Z"
                    fill="#077898"/>
              <path d="M7.25 7H8.25V11.5H7.25" stroke="#077898" stroke-width="1.2"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>No forecast data available</span>
          </div>
        </div>`;
		}

		return `
      <div data-testid="tooltip${name}" id="tooltip-${name}" style="color:#111; min-width:180px; padding:4px 6px">
        <div style="font-weight:600; margin-bottom:2px; font-size:16px;">${name}</div>
        <div style="color:#6B7280; font-size:12px; margin-bottom:2px;">${type}</div>
        <div style="display:flex; align-items:center; gap:6px; font-size:12px">
          <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${statusColor};box-shadow:0 0 0 2px rgba(0,0,0,0.05);"></span>
          <span style="font-weight:400; font-size:14px; color:#6B7280;">${statusText}${sitePredictionAlert}</span>
        </div>
      </div>`;
	};

	const networkTooltip = (feature) => {
		const properties = feature?.properties ?? {};
		const geometryType = feature?.geometry?.type ?? '';

		const isLink =
			geometryType === 'LineString' ||
			geometryType === 'MultiLineString' ||
			properties.type === 'link';

		const name = properties.INLETNODE ?? properties.name ?? 'Network feature';

		const type = isLink ? 'link' : 'node';

		return `
			<div
				style="
					color:#111827;
					padding:2px 2px;
				"
			>
				<div
					style="
						margin-bottom:2px;
						font-size:14px;
						display:flex;
					"
				>
					<div style="font-weight:600; padding-right:4px;">Name: </div> ${name}
				</div>

				<div
					style="
						font-size:14px;
						margin-bottom:6px;
						display:flex;
					"
				>
					<div style="font-weight:600; padding-right:4px;">Type: </div> ${type}
				</div>
			</div>
		`;
	};

	const getLinkedSiteAlertForAsset = useCallback(
		(assetProps) => {
			if (!sites?.length || !assetProps) return null;
			const ids = [
				assetProps.id,
				assetProps.infrastructureId,
				assetProps.measurementeInfraestructureId,
			]
				.filter(Boolean)
				.map(String);

			const match = (sites || []).find((s) => {
				const psid = s?.physicalStructure?.id;
				if (!psid || !ids.includes(String(psid))) return false;
				const hasBlock = (s.predictions?.blockages ?? []).some((b) => b.isLive);
				const hasAnom = (s.predictions?.anomalies ?? []).some((a) => a.isLive);
				const hasPump = (s.predictions?.pumps ?? []).some((a) => a.isLive);
				return hasBlock || hasAnom || hasPump;
			});
			if (!match) return null;

			const blockages = match.predictions?.blockages ?? [];
			const anomalies = match.predictions?.anomalies ?? [];
			const pumpAlerts = match.predictions?.pumps ?? [];

			const hasBlock = blockages.some((b) => b.isLive);
			const hasAnom = anomalies.some((a) => a.isLive);
			const hasPump = pumpAlerts.some((p) => p.isLive);

			const kind = kindFromFlags(hasBlock, hasAnom, hasPump);

			return {
				kind,
				url: match.url || null,
				siteId: String(match.id),
				displayName: match.displayName || match.id,
			};
		},
		[sites],
	);

	const selectAssetWithAlert = useCallback(
		(assetProps) => {
			const siteAlert = getLinkedSiteAlertForAsset(assetProps);
			onItemSelected(siteAlert ? { ...assetProps, siteAlert } : assetProps);
		},
		[getLinkedSiteAlertForAsset, onItemSelected],
	);

	// ======= Build site points (used for individual site markers) =======
	const buildSitePoints = useCallback(() => {
		if (!Array.isArray(sites)) return [];
		return sites
			.map((s) => {
				const filtered = filterSiteByAlertLayer(s, layerState);
				if (!filtered) return null;
				const { hasBlock, hasAnom, hasPump } = filtered;

				const kind = kindFromFlags(hasBlock, hasAnom, hasPump);
				if (!kind) return null;

				const linkedInfraId =
					s?.physicalStructure?.id != null ? String(s.physicalStructure.id) : null;

				let statusText;
				switch (kind) {
					case 'BLOCKAGE':
						statusText = 'Blockage';
						break;
					case 'ANOMALY':
						statusText = 'Anomaly';
						break;
					case 'PUMP':
						statusText = 'Pump';
						break;
					case 'BLOCKAGE_ANOMALY':
						statusText = 'Blockage & anomaly';
						break;
					case 'BLOCKAGE_PUMP':
						statusText = 'Blockage & pump';
						break;
					case 'ANOMALY_PUMP':
						statusText = 'Anomaly & pump';
						break;
					case 'BLOCKAGE_ANOMALY_PUMP':
						statusText = 'Blockage, anomaly & pump';
						break;
					default:
						statusText = 'Alert';
				}

				return {
					isSite: true,
					id: String(s.id),
					name: s.displayName || s.id,
					url: s.url,
					position: [s.longitude, s.latitude],
					kind,
					statusText,
					linkedInfraId,
					type: s.type,
				};
			})
			.filter(Boolean);
	}, [sites, layerState]);

	// ======= Load PNGs (badges) =======
	useEffect(() => {
		let cancelled = false;
		const size = 50;
		Promise.all([
			svgUrlToPngDataUrl(BLOCKAGE_ICON, size),
			svgUrlToPngDataUrl(ANOMALY_ICON, size),
			svgUrlToPngDataUrl(PUMP_ICON, size),
			svgUrlToPngDataUrl(BLOCKAGE_ANOMALY_ICON, size),
			svgUrlToPngDataUrl(BLOCKAGE_PUMP_ICON, size),
			svgUrlToPngDataUrl(ANOMALY_PUMP_ICON, size),
			svgUrlToPngDataUrl(BLOCKAGE_ANOMALY_PUMP_ICON, size),
		])
			.then(
				([blockagePng, anomalyPng, pumpPng, blockAnomPng, blockPumpPng, anomPumpPng, allPng]) => {
					if (!cancelled) {
						setBadgeAtlases({
							size,
							blockagePng,
							anomalyPng,
							pumpPng,
							blockAnomPng,
							blockPumpPng,
							anomPumpPng,
							allPng,
						});
					}
				},
			)
			.catch((err) => console.error('SVG → PNG (badge) failed', err));
		return () => {
			cancelled = true;
		};
	}, []);

	// ======= Build site layers (but hide those that are absorbed into asset clusters) =======
	useEffect(() => {
		if (!badgeAtlases) return;

		const base = buildSitePoints();
		sitePointsRef.current = base;

		const alignedBase = base.map((p) => {
			const assetPosition = p.linkedInfraId ? assetIndex.get(p.linkedInfraId) : null;

			if (assetPosition && assetPosition.position) {
				const assetVisible = assetMatchesLayerFilter(assetPosition.asset, layerState);

				return {
					...p,
					position: assetPosition.position,
					assetAligned: true,
					assetVisible,
				};
			}

			return {
				...p,
				assetAligned: false,
				assetVisible: false,
			};
		});

		const visible = alignedBase.filter((p) => !hiddenSiteIds.has(p.id));

		const alignedSites = visible.filter((p) => p.assetAligned && p.assetVisible);

		const freeSites = visible.filter((p) => !p.assetAligned || !p.assetVisible);

		const iconFor = (d) => {
			let url;
			switch (d.kind) {
				case 'BLOCKAGE':
					url = badgeAtlases.blockagePng;
					break;
				case 'ANOMALY':
					url = badgeAtlases.anomalyPng;
					break;
				case 'PUMP':
					url = badgeAtlases.pumpPng;
					break;
				case 'BLOCKAGE_ANOMALY':
					url = badgeAtlases.blockAnomPng;
					break;
				case 'BLOCKAGE_PUMP':
					url = badgeAtlases.blockPumpPng;
					break;
				case 'ANOMALY_PUMP':
					url = badgeAtlases.anomPumpPng;
					break;
				case 'BLOCKAGE_ANOMALY_PUMP':
					url = badgeAtlases.allPng;
					break;
				default:
					url = badgeAtlases.blockagePng;
			}
			return {
				url,
				width: badgeAtlases.size,
				height: badgeAtlases.size,
				mask: false,
				anchorY: Math.round(badgeAtlases.size / 2),
			};
		};

		const sitesAlignedLayer = new IconLayer({
			id: 'SitesLayerAligned',
			data: alignedSites,
			pickable: false,
			getIcon: iconFor,
			sizeUnits: 'pixels',
			getSize: 20,
			getPosition: (d) => d.position,
			getPixelOffset: () => badgeOffsetForZoom(viewState.zoom),
			parameters: { depthTest: false },
			updateTriggers: {
				getIcon: [badgeAtlases],
				getPixelOffset: [viewState.zoom, alignedSites.length, hiddenSiteIds],
			},
		});
		sitesAlignedLayer.zIndex = SITES_Z_INDEX;

		const sitesFreeLayer = new IconLayer({
			id: 'SitesLayerFree',
			data: freeSites,
			pickable: true,
			getIcon: iconFor,
			sizeUnits: 'pixels',
			getSize: () => siteSizeForZoom(viewState.zoom),
			getPosition: (d) => d.position,
			getPixelOffset: () => [0, 0],
			parameters: { depthTest: false },
			updateTriggers: {
				getIcon: [badgeAtlases],
				getSize: [viewState.zoom, freeSites.length],
				getPixelOffset: [viewState.zoom, freeSites.length, hiddenSiteIds],
			},
		});
		sitesFreeLayer.zIndex = SITES_Z_INDEX;

		setLayers((l) => combineLayers(l, [sitesAlignedLayer, sitesFreeLayer]));
	}, [badgeAtlases, buildSitePoints, viewState, infrastructure, hiddenSiteIds]);

	useEffect(() => {
		if (!Array.isArray(sites)) {
			setSiteIndex(null);
			return;
		}

		const feats = (sites || [])
			.map((s) => {
				const filtered = filterSiteByAlertLayer(s, layerState);
				if (!filtered) return null;
				const { hasBlock, hasAnom, hasPump } = filtered;

				const kind = kindFromFlags(hasBlock, hasAnom, hasPump);
				if (!kind) return null;

				const linkedId = s?.physicalStructure?.id != null ? String(s.physicalStructure.id) : null;
				const exactCoord = linkedId ? assetIndex.get(linkedId) : null;
				const [lon, lat] = exactCoord?.position || [s.longitude, s.latitude];

				return {
					type: 'Feature',
					properties: {
						id: String(s.id),
						kind,
					},
					geometry: { type: 'Point', coordinates: [Number(lon), Number(lat)] },
				};
			})
			.filter(Boolean);

		if (!feats.length) {
			setSiteIndex(null);
			return;
		}

		const idx = new Supercluster({
			radius: CLUSTER_RADIUS,
			minZoom: CLUSTER_MIN_ZOOM,
			maxZoom: CLUSTER_MAX_ZOOM,
			map: (p) => {
				const { hasBlock, hasAnom, hasPump } = flagsFromKind(p.kind);
				return {
					block: hasBlock ? 1 : 0,
					anom: hasAnom ? 1 : 0,
					pump: hasPump ? 1 : 0,
					ids: [p.id],
				};
			},
			reduce: (acc, props) => {
				acc.block += props.block;
				acc.anom += props.anom;
				acc.pump += props.pump;
				if (props.ids) acc.ids.push(...props.ids);
			},
			initial: () => ({ block: 0, anom: 0, pump: 0, ids: [] }),
		}).load(feats);

		setSiteIndex(idx);
	}, [sites, layerState, assetIndex]);

	useEffect(() => {
		if (!infrastructure) return;
		const filteredAssets = infrastructure
			.filter((d) => hideLayer(d.infrastructureTypeName ?? d.type) === false)
			.filter((d) => assetMatchesLayerFilter(d, layerState));
		parseAssetData(filteredAssets, true, getSvgIconUrl, {
			predictedAt: !isDefault ? Number(timeBarSelection) : null,
			forecastIndex: predictedImpacts ?? null,
		}).then((geoJsonData) => {
			const index = new Supercluster({
				radius: CLUSTER_RADIUS,
				maxZoom: CLUSTER_MAX_ZOOM,
				minZoom: CLUSTER_MIN_ZOOM,
			});
			setClusteredAssets(index.load(geoJsonData));
		});
	}, [infrastructure, isDefault, timeBarSelection, predictedImpacts, layerState]);

	useEffect(() => {
		if (!clusteredAssets) return;
		let clusteredAssetsData;
		try {
			clusteredAssetsData = clusteredAssets.getClusters(viewState.bounds, viewState.zoom);
		} catch (e) {
			console.error('Error getting clustered assets:', e);
		}
		if (clusteredAssetsData) {
			const geoJsonIconLayers = [
				generateGeoJsonIconLayer('cluster', clusteredAssetsData, getClusterChildren, ICON_Z_INDEX, {
					isDefault,
					playTs: timeBarSelection,
					getPredictedStatus: (id) => getImpactedStatusNow(id),
					rainGaugeEnabled: getRainGaugeEnabled(layerState),
				}),
			];
			setLayers((l) => combineLayers(l, geoJsonIconLayers));
		}
	}, [
		clusteredAssets,
		viewState?.bounds,
		viewState?.zoom,
		isDefault,
		timeBarSelection,
		predictedImpacts,
		layerState,
	]);

	useEffect(() => {
		if (!hasNetworkData(links)) {
			return;
		}

		const networkLayers = generateInfrastructurePaths(
			links,
			INFRASTRUCTURE_Z_INDEX
		);

		const showNetworkNodes =
			Number(viewState?.zoom ?? 0) >= NETWORK_NODE_MIN_ZOOM;

		setLayers((previousLayers) => {
			const withoutOldNetworkLayers = previousLayers.filter(
				(layer) =>
					layer.id !== 'infrastructure-links' &&
					layer.id !== 'infrastructure-nodes'
			);

			const filteredNetworkLayers = networkLayers.filter((layer) => {
				if (layer.id === 'infrastructure-nodes') {
					return showNetworkNodes;
				}

				return true;
			});

			return combineLayers(
				withoutOldNetworkLayers,
				filteredNetworkLayers
			);
		});
	}, [links, activeLocation, viewState?.zoom]);

	useEffect(() => {
		const pickLastCoord = (v) => (Array.isArray(v) ? v.at(-1) : v);
		const idx = new globalThis.Map();

		if (Array.isArray(infrastructure)) {
			for (const a of infrastructure) {
				const { latitude, longitude } = pickLastCoord(a?.coordinates) || {};
				const key = a?.infrastructureId ?? a?.measurementeInfraestructureId ?? a?.id;
				const lon = Number(longitude);
				const lat = Number(latitude);

				if (key != null && Number.isFinite(lon) && Number.isFinite(lat)) {
					idx.set(String(key), {
						position: [lon, lat],
						asset: a,
					});
				}
			}
		}

		assetIndexRef.current = idx;
	}, [infrastructure]);

	useEffect(() => {
		if (showWeatherOverlay && timeBarSelection) {
			(async () => {
				const tileLayers = await generateTileLayers.tomorrowIo(
					WEATHER_Z_INDEX,
					timeBarSelection,
					weatherOverlayKey,
				);

				setLayers((prev) => combineLayers(removeLayers(prev, 'TileLayer'), [tileLayers]));
			})();
		} else {
			setLayers((prev) => removeLayers(prev, 'TileLayer'));
		}
	}, [timeBarSelection, showWeatherOverlay, weatherOverlayKey]);

	useEffect(() => {
		setLayers([]);
		setClusteredAssets(null);
		setSiteIndex(null);
		setBadgePoints([]);
		setHiddenSiteIds(new Set());
		setStackedAssetInfo(null);
		onItemSelected?.(null);
	}, [activeLocation, onItemSelected]);

	// ======= Apply site badges & hide sites that are within an asset cluster =======
	useEffect(() => {
		if (!clusteredAssets || !siteIndex || !viewState?.bounds) {
			setBadgePoints([]);
			setHiddenSiteIds(new Set());
			return;
		}

		const vp = new WebMercatorViewport({ ...viewState });
		const toPx = ([lon, lat]) => vp.project([lon, lat]);

		const assetNodes = clusteredAssets
			.getClusters(viewState.bounds, viewState.zoom)
			.filter((f) => f.properties?.cluster === true);

		const siteNodes = siteIndex.getClusters(viewState.bounds, viewState.zoom);

		const nextBadges = [];
		const nextHidden = new Set();

		const summarizeSiteNode = (node) => {
			if (node.properties.cluster) {
				const leaves = siteIndex.getLeaves(node.properties.cluster_id, Infinity);
				let block = 0,
					anom = 0,
					pump = 0;
				for (const lf of leaves) {
					const { hasBlock, hasAnom, hasPump } = flagsFromKind(lf.properties.kind);
					if (hasBlock) block++;
					if (hasAnom) anom++;
					if (hasPump) pump++;
					nextHidden.add(lf.properties.id);
				}
				return { block, anom, pump };
			} else {
				const { hasBlock, hasAnom, hasPump } = flagsFromKind(node.properties.kind);
				nextHidden.add(node.properties.id);
				return {
					block: hasBlock ? 1 : 0,
					anom: hasAnom ? 1 : 0,
					pump: hasPump ? 1 : 0,
				};
			}
		};

		for (const ac of assetNodes) {
			const cxy = toPx(ac.geometry.coordinates);
			let block = 0,
				anom = 0,
				pump = 0;

			for (const sn of siteNodes) {
				const sxy = toPx(sn.geometry.coordinates);
				const dx = sxy[0] - cxy[0],
					dy = sxy[1] - cxy[1];
				if (Math.hypot(dx, dy) <= CLUSTER_RADIUS) {
					const sum = summarizeSiteNode(sn);
					block += sum.block;
					anom += sum.anom;
					pump += sum.pump;
				}
			}

			if (block || anom || pump) {
				const kind = kindFromFlags(block > 0, anom > 0, pump > 0);
				if (kind) {
					nextBadges.push({
						position: ac.geometry.coordinates,
						kind,
						count: ac.properties?.point_count || 1,
					});
				}
			}
		}

		setBadgePoints(nextBadges);
		setHiddenSiteIds(nextHidden);
	}, [clusteredAssets, siteIndex, viewState, layerState]);

	// ======= Site badge layer (drawn on top of asset clusters) =======
	useEffect(() => {
		if (!badgeAtlases) return;

		const clusterBadgeLayer = new IconLayer({
			id: 'ClusterAlertBadge',
			data: badgePoints,
			pickable: false,
			getIcon: (d) => {
				let url;
				switch (d.kind) {
					case 'BLOCKAGE':
						url = badgeAtlases.blockagePng;
						break;
					case 'ANOMALY':
						url = badgeAtlases.anomalyPng;
						break;
					case 'PUMP':
						url = badgeAtlases.pumpPng;
						break;
					case 'BLOCKAGE_ANOMALY':
						url = badgeAtlases.blockAnomPng;
						break;
					case 'BLOCKAGE_PUMP':
						url = badgeAtlases.blockPumpPng;
						break;
					case 'ANOMALY_PUMP':
						url = badgeAtlases.anomPumpPng;
						break;
					case 'BLOCKAGE_ANOMALY_PUMP':
						url = badgeAtlases.allPng;
						break;
					default:
						url = badgeAtlases.blockagePng;
				}
				return {
					url,
					width: badgeAtlases.size,
					height: badgeAtlases.size,
					mask: false,
					anchorY: Math.round(badgeAtlases.size / 2),
				};
			},
			sizeUnits: 'pixels',
			getSize: () => siteSizeForZoom(viewState.zoom),
			getPosition: (d) => d.position,
			getPixelOffset: (d) =>
				clampOffsetToCircle(badgeOffsetForZoom(viewState.zoom), clusterMarkerRadiusPx(d.count)),
			parameters: { depthTest: false },
			updateTriggers: { getIcon: [badgePoints], getPixelOffset: [viewState.zoom, badgePoints] },
		});
		clusterBadgeLayer.zIndex = BADGE_Z_INDEX;

		setLayers((l) => combineLayers(l, [clusterBadgeLayer]));
	}, [badgePoints, badgeAtlases, viewState.zoom]);

	const onIconSelected = (event) => {
		if (!event.object) return;

		const layerId = event.layer?.id;

		// Network features are hoverable but not clickable.
		if (
			layerId === 'infrastructure-links' ||
			layerId === 'infrastructure-nodes'
		) {
			return;
		}

		// Existing site behaviour
		if (
			layerId === 'SitesLayerFree' &&
			event.object?.isSite
		) {
			const url = event.object.url;

			if (url) {
				window.open(url, '_blank', 'noopener,noreferrer');
			}

			return;
		}

		// Existing cluster behaviour
		if (event.object.properties.cluster === true) {
			const clusterId =
				event.object.properties.cluster_id;

			const points =
				event.object.geometry.coordinates;

			const zoomLevel =
				clusteredAssets.getClusterExpansionZoom(clusterId);

			if (zoomLevel >= CLUSTER_STACKED_ASSET_ZOOM) {
				const assets =
					clusteredAssets.getChildren(clusterId);

				setStackedAssetInfo(
					stackedAssetInfo
						? null
						: {
							x: event.pixel[0],
							y: event.pixel[1],
							anchorX: event.pixel[0],
							assets: assets.map(
								(a) => a.properties
							),
						}
				);
			} else {
				setViewState({
					...viewState,
					zoom: zoomLevel,
					latitude: points[1],
					longitude: points[0],
				});
			}

			return;
		}

		// Existing asset behaviour
		selectAssetWithAlert(
			event.object.properties
		);
	};

	const onStackedAssetSelected = (asset) => {
		selectAssetWithAlert(asset);
		setStackedAssetInfo(null);
	};

	const toSafeType = (type) =>
		String(type || '')
			.trim()
			.toLowerCase()
			.replace(/\s+/g, '_');

	const ICON_NUDGE = {
		conduit: { x: 4, y: -1, size: 34 },
	};

	
	const LocationTypeIcon = ({ type }) => {
		const safeType = toSafeType(type);
		const iconSrc = `/img/nwlIcons/new/${safeType}.svg`;
		const nudge = ICON_NUDGE[safeType] ?? { x: 0, y: 0 };
		const glyphSize = nudge.size ?? 34;
		
		return (
			<div className="unknown-locations-icon">
				<div
					className="unknown-locations-icon-mask"
					style={{
						width: glyphSize,
						height: glyphSize,
						WebkitMaskImage: `url(${iconSrc})`,
						maskImage: `url(${iconSrc})`,
						transform: `translate(${nudge.x}px, ${nudge.y}px)`,
					}}
				/>
			</div>
		);
	};

	useEffect(() => {
		const handler = (e) => {
			const lon = Number(e?.detail?.longitude);
			const lat = Number(e?.detail?.latitude);
			const zoom = Number(e?.detail?.zoom ?? 18);
			const assetImage = e?.detail?.imgType ?? 'UNKNOWN';
			const assetName = e?.detail?.name ?? null;

			if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;

			setFocusPin({ longitude: lon, latitude: lat, imgType: assetImage, name: assetName });

			setViewState((prev) => ({
				...prev,
				longitude: lon,
				latitude: lat,
				zoom: Number.isFinite(zoom) ? zoom : (prev?.zoom ?? 18),
			}));
		};

		window.addEventListener('nwl:map-focus', handler);
		return () => window.removeEventListener('nwl:map-focus', handler);
	}, [setViewState]);

	useEffect(() => {
		if (!focusPin) return;
		const t = setTimeout(() => setFocusPin(null), 10000);
		return () => clearTimeout(t);
	}, [focusPin]);

	const hasAssetNear = useCallback(
		({ longitude, latitude }) => {
			const vp = new WebMercatorViewport({ ...viewState });
			const [fx, fy] = vp.project([longitude, latitude]);

			const THRESHOLD_PX = 10;

			for (const { position } of assetIndexRef.current.values()) {
				if (!position) continue;
				const [ax, ay] = vp.project(position);
				if (Math.hypot(ax - fx, ay - fy) <= THRESHOLD_PX) return true;
			}
			return false;
		},
		[viewState],
	);

	useEffect(() => {
		if (!focusPin) {
			setFocusHasAsset(false);
			return;
		}
		setFocusHasAsset(hasAssetNear(focusPin));
	}, [focusPin, hasAssetNear]);

	const focusPx = useMemo(() => {
		if (!focusPin || !deckHostRef.current) return null;

		try {
			const vp = new WebMercatorViewport({ ...viewState });
			const [x, y] = vp.project([focusPin.longitude, focusPin.latitude]);

			const rect = deckHostRef.current.getBoundingClientRect();
			return {
				left: rect.left + x,
				top: rect.top + y,
			};
		} catch {
			return null;
		}
	}, [focusPin, viewState]);

	return (
		<div className="nwl-deck-host" ref={deckHostRef}>
			<DeckGL
				ref={deckRef}
				viewState={viewState}
				controller={MAP_CONTROLLER_OPTIONS}
				layers={orderLayers(layers)}
				onViewStateChange={({ viewState }) => handleViewStateChange(viewState)}
				data-testid={'map-container'}
				onClick={onIconSelected}
				getCursor={({ isDragging, isHovering }) =>
					isDragging ? 'grabbing' : isHovering ? 'pointer' : 'grab'
				}
				width={'100vw'}
				getTooltip={({ object, layer }) => {
					if (!object) return null;

					// Existing asset tooltip
					if (
						object.properties &&
						object.properties.cluster === false
					) {
						return {
							html: assetTooltipHtml(object.properties),
							style: {
								top: 0,
								left: 0,
								backgroundColor: '#fff',
								opacity: 1,
								borderRadius: '8px',
							},
						};
					}

					// Existing site tooltip
					if (
						layer &&
						layer.id === 'SitesLayerFree' &&
						object.isSite
					) {
						return {
							html: siteTooltip(object),
							style: {
								top: 0,
								left: 0,
								backgroundColor: '#fff',
								opacity: 1,
								borderRadius: '8px',
							},
						};
					}

					// Network tooltip
					if (
						layer &&
						(
							layer.id === 'infrastructure-links' ||
							layer.id === 'infrastructure-nodes'
						) &&
						Number(viewState?.zoom ?? 0) >= NETWORK_NODE_MIN_ZOOM
					) {
						return {
							html: networkTooltip(object),
							style: {
								top: 0,
								left: 0,
								backgroundColor: '#fff',
								opacity: 1,
								borderRadius: '8px',
							},
						};
					}
					return null;
				}}
			>
				<StaticMap
					ref={mapRef}
					mapboxAccessToken={process.env.VITE_MAPBOX_API_TOKEN}
					mapStyle={MAPBOX_MAP_STYLE}
					projection="globe"
					style={{ position: 'relative', width: '100%', height: '100%' }}
					onLoad={() => {
						const map = mapRef.current?.getMap?.();
						map?.resize?.();
						syncBoundsFromMap();
					}}
				>
					{focusPin && focusHasAsset && (
						<Marker longitude={focusPin.longitude} latitude={focusPin.latitude} anchor="center">
							<div className="nwl-focus-anchor" aria-hidden="true">
								<div className="nwl-ripple">
									<span className="nwl-ripple__ring r1" />
									<span className="nwl-ripple__ring r2" />
									<span className="nwl-ripple__ring r3" />
								</div>
							</div>
						</Marker>
					)}
				</StaticMap>

				{children}
			</DeckGL>

			{focusPin && focusPx && !focusHasAsset && (
				<div
					className="nwl-focus-overlay-fixed"
					style={{ left: focusPx.left, top: focusPx.top }}
					aria-hidden="true"
				>
					{(focusPin?.name || focusPin?.imgType) && !focusHasAsset && (
						<div
							data-testid={`tooltip-${focusPin.name ?? 'focus'}`}
							id={`tooltip-${focusPin.name ?? 'focus'}`}
							className="nwl-focus-tooltip"
							aria-live="polite"
						>
							<div className="nwl-focus-tooltip__title">{focusPin.name}</div>
							<div className="nwl-focus-tooltip__subtitle">
								{String(focusPin.imgType || '').toUpperCase()}
							</div>
						</div>
					)}

					<div className="nwl-ripple" aria-hidden="true">
						{!focusHasAsset && (
							<>
								<svg
									className="nwl-focus-pin__pin"
									width="32"
									height="46"
									viewBox="0 0 32 46"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										opacity="0.25"
										d="M23.7413 43.9124C23.746 44.1035 23.5617 44.2929 23.1988 44.4697C22.836 44.6465 22.3018 44.8072 21.6269 44.9427C20.9521 45.0781 20.1499 45.1856 19.2665 45.2589C18.3831 45.3323 17.4358 45.37 16.4791 45.37C15.5224 45.37 14.5751 45.3323 13.6917 45.2589C12.8083 45.1856 12.0061 45.0781 11.3313 44.9427C10.6565 44.8072 10.1223 44.6465 9.75941 44.4697C9.39656 44.2929 9.21218 44.1035 9.21688 43.9124C9.21218 43.7214 9.39656 43.532 9.75941 43.3552C10.1223 43.1784 10.6565 43.0176 11.3313 42.8822C12.0061 42.7467 12.8083 42.6393 13.6917 42.5659C14.5751 42.4926 15.5224 42.4548 16.4791 42.4548C17.4358 42.4548 18.3831 42.4926 19.2665 42.5659C20.1499 42.6393 20.9521 42.7467 21.6269 42.8822C22.3018 43.0176 22.836 43.1784 23.1988 43.3552C23.5617 43.532 23.746 43.7214 23.7413 43.9124Z"
										fill="#74788D"
									/>
									<path
										d="M15.9967 1C7.71229 1.01145 1 7.61456 1 15.758C1 21.1512 3.94348 25.868 8.33827 28.443C12.6262 31.5814 15.5293 36.4432 15.9877 41.985L15.9966 42L16.0004 41.9938L16.0029 42L16.0118 41.985C16.4707 36.4427 19.3734 31.5812 23.6617 28.443C28.0567 25.8684 31 21.1512 31 15.758C31 7.61456 24.2877 1.01154 16.0033 1C16.0011 0.999998 15.999 1 15.9969 1H15.9967Z"
										fill="white"
									/>
									<path
										d="M0 15.7578C0.000123594 7.04632 7.1757 0.0123216 15.9951 0H16.0049L16.0029 1C16.001 0.999998 15.999 1 15.9971 1L15.6094 1.00488C7.50413 1.21781 1.00012 7.74174 1 15.7578L1.00879 16.2617C1.18563 21.4439 4.08055 25.9488 8.33789 28.4434C12.6259 31.5818 15.5289 36.4435 15.9873 41.9854L15.9971 42L16 41.9941L16.0029 42L16.0117 41.9854C16.4706 36.443 19.3738 31.5815 23.6621 28.4434C27.9197 25.9492 30.8144 21.444 30.9912 16.2617L31 15.7578C30.9999 7.74174 24.4959 1.21789 16.3906 1.00488L16.0029 1L16.0049 0C24.8243 0.0124105 31.9999 7.04631 32 15.7578C32 21.5025 28.8756 26.5194 24.2236 29.2705C20.1732 32.2466 17.4416 36.8405 17.0088 42.0674L16.9893 42.2969L16.8721 42.4951L16.8633 42.5107L15.835 44.2412L15.1162 42.4756L15.0098 42.2969L14.9912 42.0674C14.5588 36.8407 11.8259 32.2469 7.77539 29.2705C3.12415 26.519 0 21.5023 0 15.7578Z"
										fill="white"
									/>
								</svg>

								{focusPin.imgType && (
									<div className="nwl-focus-pin__icon">
										<LocationTypeIcon type={focusPin.imgType} />
									</div>
								)}
							</>
						)}

						<span className="nwl-ripple__ring r1" />
						<span className="nwl-ripple__ring r2" />
						<span className="nwl-ripple__ring r3" />
					</div>
				</div>
			)}

			{stackedAssetInfo && (
				<StackedAssetModal
					stackedAssetInfo={stackedAssetInfo}
					onStackedAssetSelected={onStackedAssetSelected}
					isDefault={isDefault}
					siteAlerts={siteAlerts}
				/>
			)}
		</div>
	);
};

export default Map;